import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastContext } from '@contexts/ToastContext';
import { useAuthStore } from '@hooks/useStores';
import $api from '@http';

export interface Collaborator {
  userId: string;
  name: string;
  email: string;
  permission: 'read' | 'edit';
  isOwner?: boolean;
}

export interface User {
  id?: string;
  _id?: string;
  name?: string;
  email?: string;
  login?: string;
  username?: string;
}

export const useShareModal = (noteId: string, open: boolean, onAccessChanged?: () => void) => {
  const { t } = useTranslation();
  const toast = useToastContext();
  const authStore = useAuthStore();
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [subnoteIds, setSubnoteIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && noteId) {
      loadUsers().then(() => {
        loadAccessList();
      });
      loadSubnoteIds();
    }
  }, [open, noteId]);

  useEffect(() => {
    if (open && users.length > 0) {
      loadAccessList();
    }
  }, [users]);

  const loadSubnoteIds = async () => {
    try {
      const res = await $api.get('/notes');
      const notes: Array<{ id: string; parentId?: string }> = Array.isArray(res.data) ? res.data : [];
      const collect = (parentId: string): string[] => {
        const children = notes.filter(n => n.parentId === parentId);
        return children.flatMap(c => [c.id, ...collect(c.id)]);
      };
      setSubnoteIds(collect(noteId));
    } catch {
      setSubnoteIds([]);
    }
  };

  const loadAccessList = async () => {
    try {
      const res = await $api.get(`/notes/${noteId}/access`);
      const accessList = res.data || [];
      
      // Map access list to collaborators
      const collabs: Collaborator[] = [];
      
      // Add owner
      if (authStore.user?.id) {
        collabs.push({
          userId: authStore.user.id,
          name: authStore.user.username || t('share.you'),
          email: authStore.user.email || '',
          permission: 'edit',
          isOwner: true,
        });
      }

      // Load user details for each access entry
      for (const access of accessList) {
        // Try to get user info from users list or use placeholder
        const user = users.find(u => u.id === access.userId || u.id === access.userId?.toString());
        
        if (user) {
          collabs.push({
            userId: access.userId,
            name: user.name || t('share.userFallback', { id: access.userId }),
            email: user.email || '',
            permission: access.permission,
          });
        } else {
          // Use placeholder if user not found
          collabs.push({
            userId: access.userId,
            name: t('share.userFallback', { id: String(access.userId).slice(0, 8) }),
            email: '',
            permission: access.permission,
          });
        }
      }

      setCollaborators(collabs);
    } catch (err: any) {
      console.error('Failed to load access list:', err);
      toast.error(t('share.toast.loadAccessFailed'));
    }
  };

  const loadUsers = async () => {
    try {
      const res = await $api.get('/users');
      const usersData = Array.isArray(res.data) ? res.data : [];
      // Normalize user data - handle both DTO format and raw model format
      const normalizedUsers = usersData.map((user: any) => ({
        id: user.id || user._id?.toString() || user._id,
        name: user.name || user.username || user.login || '',
        email: user.email || '',
        login: user.login || user.username || '',
      }));
      setUsers(normalizedUsers);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleInvite = async (userId: string, permission: 'read' | 'edit', cascadeIds: string[] = []) => {
    if (!authStore.user?.isActivated) {
      toast.warning(t('share.toast.activateToInvite'));
      return false;
    }

    // Check if user is already a collaborator
    if (collaborators.some(c => c.userId === userId || c.userId === userId.toString())) {
      toast.warning(t('share.toast.alreadyHasAccess'));
      return false;
    }

    setLoading(true);
    try {
      await $api.post(`/notes/${noteId}/access`, {
        userId: userId,
        permission: permission,
      });

      if (cascadeIds.length > 0) {
        await Promise.allSettled(
          cascadeIds.map((id) => $api.post(`/notes/${id}/access`, { userId, permission }))
        );
      }

      await loadAccessList();
      onAccessChanged?.();
      return true;
    } catch (err: any) {
      console.error('Failed to add access:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCollaborator = async (userId: string, cascadeIds: string[] = []) => {
    if (!authStore.user?.isActivated) {
      toast.warning(t('share.toast.activateToManage'));
      return;
    }

    if (userId === authStore.user?.id) {
      toast.warning(t('share.toast.cannotRemoveOwner'));
      return;
    }

    setLoading(true);
    try {
      await $api.delete(`/notes/${noteId}/access/${userId}`);

      if (cascadeIds.length > 0) {
        await Promise.allSettled(
          cascadeIds.map((id) => $api.delete(`/notes/${id}/access/${userId}`))
        );
      }

      toast.success(t('share.toast.accessRemoved'));
      await loadAccessList();
      onAccessChanged?.();
    } catch (err: any) {
      console.error('Failed to remove access:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePermission = async (userId: string, newPermission: 'read' | 'edit', cascadeIds: string[] = []) => {
    if (!authStore.user?.isActivated) {
      toast.warning(t('share.toast.activateToManage'));
      return;
    }

    setLoading(true);
    try {
      await $api.patch(`/notes/${noteId}/access/${userId}`, {
        permission: newPermission,
      });

      if (cascadeIds.length > 0) {
        await Promise.allSettled(
          cascadeIds.map((id) => $api.patch(`/notes/${id}/access/${userId}`, { permission: newPermission }))
        );
      }

      toast.success(t('share.toast.permissionUpdated'));
      await loadAccessList();
    } catch (err: any) {
      console.error('Failed to update permission:', err);
    } finally {
      setLoading(false);
    }
  };

  return {
    collaborators,
    users,
    subnoteIds,
    loading,
    handleInvite,
    handleRemoveCollaborator,
    handleUpdatePermission,
  };
};

